import { fetchJSON as fetchAPI } from './client';

export type GrowthDashboard = {
  tenant_id: string;
  growth_score: number;
  opportunities_open: number;
  priority_opportunities: number;
  revenue_at_risk: number;
  revenue_potential: number;
  active_recommendations: number;
  executions_running: number;
  completed_improvements: number;
  realized_impact: number;
  generated_at: string;
};

export type GrowthOpportunity = {
  id: string;
  type: string;
  status: string;
  title: string;
  business_problem: string;
  evidence?: string;
  asin?: string;
  brand?: string;
  category?: string;
  expected_revenue_impact: number;
  expected_profit_impact: number;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  created_at: string;
};

export type GrowthRecommendation = {
  id: string;
  opportunity_id?: string;
  type: string;
  title: string;
  recommendation: string;
  business_problem: string;
  evidence?: string;
  expected_revenue_impact: number;
  expected_profit_impact: number;
  estimated_effort: string;
  confidence_score: number;
  dependencies?: string[];
  requires_approval: boolean;
  priority_rank: number;
};

export type GrowthScores = {
  organization_score: number;
  brand_score: number;
  category_score: number;
  product_score: number;
  opportunity_score: number;
  execution_readiness_score: number;
  organization_level: string;
};

export type ExecutionRecord = {
  id: string;
  opportunity_id?: string;
  recommendation_id?: string;
  status: string;
  title: string;
  workflow_id?: string;
  copilot_session_id?: string;
  publishing_job_id?: string;
  governance_approved: boolean;
  started_at?: string;
  completed_at?: string;
  created_at: string;
};

export type BusinessImpact = {
  revenue_at_risk: number;
  revenue_potential: number;
  realized_revenue: number;
  realized_profit: number;
  completed_actions: number;
  pending_actions: number;
  avg_confidence: number;
};

export type SeasonalInsight = {
  id: string;
  title: string;
  description?: string;
  event?: string;
  readiness_pct: number;
  days_until?: number;
  priority?: string;
};

export type CompetitiveInsight = {
  id: string;
  asin?: string;
  category?: string;
  insight: string;
  opportunity?: string;
  score: number;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_AMAZONGROWTH_API ?? '/api/v1/amazongrowth';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const amazongrowthApi = {
  dashboard: () => fetchJSON<GrowthDashboard>('/dashboard'),
  listOpportunities: (status?: string) =>
    fetchJSON<GrowthOpportunity[]>(`/opportunities${status ? `?status=${status}` : ''}`),
  getOpportunity: (id: string) => fetchJSON<GrowthOpportunity>(`/opportunities/${id}`),
  approveOpportunity: (id: string, approvedBy?: string) =>
    fetchJSON<GrowthOpportunity>(`/opportunities/${id}/approve`, { method: 'POST', body: JSON.stringify({ approved_by: approvedBy }) }),
  listRecommendations: () => fetchJSON<GrowthRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<GrowthRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  scores: () => fetchJSON<GrowthScores>('/scores'),
  listExecutions: () => fetchJSON<ExecutionRecord[]>('/execution'),
  startExecution: (recommendationId: string) =>
    fetchJSON<ExecutionRecord>('/execution/start', {
      method: 'POST',
      body: JSON.stringify({ recommendation_id: recommendationId, use_copilot: true, use_workflow: true }),
    }),
  businessImpact: () => fetchJSON<BusinessImpact>('/impact'),
  seasonal: () => fetchJSON<SeasonalInsight[]>('/seasonal'),
  competitive: () => fetchJSON<CompetitiveInsight[]>('/competitive'),
  listActivity: () => fetchJSON<ActivityEntry[]>('/activity'),
};
